import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import LinkProgressDialog from "./LinkProgressDialog";
import LinkProgressSingleDialog, { LinkStatus } from "./LinkProgressSingleDialog";
import RelinkWarningDialog from "./RelinkWarningDialog";

export interface CoachingClient {
  id: string;
  name: string;
  company: string;
}

interface LinkClientDialogProps {
  open: boolean;
  clients: CoachingClient[];
  selectedCount: number;
  existingLinkages?: Record<string, string>; // eventId -> clientName
  onClose: () => void;
  // Returning a Promise (or throwing) lets the progress dialog reflect success/error.
  onLink: (clientId: string) => Promise<void> | void;
}

const LinkClientDialog = ({
  open,
  clients,
  selectedCount,
  existingLinkages = {},
  onClose,
  onLink,
}: LinkClientDialogProps) => {
  const { t } = useTranslation();
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [progressClientName, setProgressClientName] = useState("");
  const [showRelinkWarning, setShowRelinkWarning] = useState(false);
  const [existingClientName, setExistingClientName] = useState("");
  const [relinkConfirmed, setRelinkConfirmed] = useState(false);
  const [linkStatus, setLinkStatus] = useState<LinkStatus>("pending");

  const startLinking = (clientId: string, clientNameForProgress: string) => {
    setProgressClientName(clientNameForProgress);
    setLinkStatus("pending");
    setShowProgress(true);
    // Fire the linkage immediately; the progress dialog reflects success/error
    // once the promise settles.
    Promise.resolve()
      .then(() => onLink(clientId))
      .then(() => setLinkStatus("success"))
      .catch(() => setLinkStatus("error"));
  };

  const handleLink = () => {
    if (!selectedClientId) return;

    const selectedClient = clients.find(
      (client) => client.id === selectedClientId,
    );
    const newClientName = selectedClient?.name ?? "";

    // Check if any events are already linked to a different client
    const linkedClientNames = Object.values(existingLinkages);
    const alreadyLinkedToDifferent = linkedClientNames.find(
      (clientName) => clientName && clientName !== newClientName,
    );

    if (alreadyLinkedToDifferent && !relinkConfirmed) {
      // Show relink warning before proceeding
      setExistingClientName(alreadyLinkedToDifferent);
      setProgressClientName(newClientName);
      setShowRelinkWarning(true);
    } else {
      // No conflicting linkage or already confirmed, fire the link and show progress
      startLinking(selectedClientId, newClientName);
    }
  };

  const handleRelinkConfirm = () => {
    if (!selectedClientId) return;
    const selectedClient = clients.find(
      (client) => client.id === selectedClientId,
    );
    const newClientName = selectedClient?.name ?? progressClientName;
    setShowRelinkWarning(false);
    setRelinkConfirmed(true);
    startLinking(selectedClientId, newClientName);
  };

  const handleRelinkCancel = () => {
    setShowRelinkWarning(false);
    setExistingClientName("");
  };

  const handleProgressComplete = () => {
    // Linkage already fired when progress started; only reset UI state here.
    setShowProgress(false);
    setSelectedClientId(null);
    setRelinkConfirmed(false);
    setExistingClientName("");
    setProgressClientName("");
    setLinkStatus("pending");
  };

  const handleClose = () => {
    setSelectedClientId(null);
    setShowProgress(false);
    setShowRelinkWarning(false);
    setProgressClientName("");
    setExistingClientName("");
    setRelinkConfirmed(false);
    setLinkStatus("pending");
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !showProgress && !showRelinkWarning}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth slotProps={{ paper: { sx: { borderRadius: "8px", bgcolor: "#fff" } } }}
      >
        <DialogTitle>
          {t("coachingNavigator.coachingCalendar.linkClientTitle")}
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 1.5
            }}>
            {t("coachingNavigator.coachingCalendar.linkClientDescription", {
              count: selectedCount,
            })}
          </Typography>
          <List disablePadding>
            {clients.map((client) => (
              <ListItemButton
                key={client.id}
                selected={selectedClientId === client.id}
                onClick={() => setSelectedClientId(client.id)}
                sx={{
                  borderRadius: 3,
                  mb: 0.5,
                  "&.Mui-selected": {
                    bgcolor: "#242C63",
                    color: "#fff",
                    "&:hover": { bgcolor: "#1a2050" },
                    "& .MuiListItemText-secondary": {
                      color: "rgba(255,255,255,0.7)",
                    },
                  },
                }}
              >
                <ListItemText
                  primary={client.name}
                  secondary={client.company}
                />
                {selectedClientId === client.id && (
                  <CheckIcon fontSize="small" />
                )}
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            {t("button.cancel")}
          </Button>
          <Button
            onClick={handleLink}
            variant="contained"
            disabled={!selectedClientId}
          >
            {t("coachingNavigator.coachingCalendar.linkToClient")}
          </Button>
        </DialogActions>
      </Dialog>
      {selectedCount === 1 ? (
        <LinkProgressSingleDialog
          open={showProgress}
          status={linkStatus}
          onComplete={handleProgressComplete}
        />
      ) : (
        <LinkProgressDialog
          open={showProgress}
          status={linkStatus}
          onComplete={handleProgressComplete}
        />
      )}
      <RelinkWarningDialog
        open={showRelinkWarning}
        existingClientName={existingClientName}
        newClientName={progressClientName}
        onConfirm={handleRelinkConfirm}
        onCancel={handleRelinkCancel}
      />
    </>
  );
};

export default LinkClientDialog;
